import React, { useEffect, useMemo, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faTrashAlt, faSyncAlt } from '@fortawesome/free-solid-svg-icons';

import { saveImages } from './providerData';

const MAX_BYTES = 5 * 1024 * 1024;
const LOGO_MIN = { width: 300, height: 150 };
const PHOTO_MIN = { width: 800, height: 600 };

const readSize = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    resolve({ width: image.naturalWidth, height: image.naturalHeight });
    URL.revokeObjectURL(url);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Imagem inválida'));
  };
  image.src = url;
});

async function checkFile(file, min) {
  if (!file.type.startsWith('image/')) return 'Escolha um arquivo de imagem (JPG ou PNG).';
  if (file.size > MAX_BYTES) return 'A imagem passa de 5 MB. Escolha uma menor.';
  try {
    const size = await readSize(file);
    if (size.width < min.width || size.height < min.height) {
      return `A imagem precisa ter ao menos ${min.width}×${min.height} px.`;
    }
  } catch (err) {
    return 'Não foi possível ler a imagem.';
  }
  return null;
}

const previewOf = (slot) => (slot ? slot.preview || slot.url : null);

function Slot({ slot, label, min, onPick, onRemove, wide }) {
  const preview = previewOf(slot);
  const inputId = `pv-file-${label.replace(/\W+/g, '-').toLowerCase()}`;

  const handleChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const problem = await checkFile(file, min);
    if (problem) {
      toastr.warning(problem);
      return;
    }
    onPick({ file, preview: URL.createObjectURL(file) });
  };

  return (
    <div className={`pv-slot${wide ? ' pv-slot-wide' : ''}${preview ? ' has-image' : ''}`}>
      <input id={inputId} type='file' accept='image/jpeg,image/png,image/webp' className='pv-sr-only' onChange={handleChange} />
      {preview ? (
        <>
          <img src={preview} alt={label} />
          <div className='pv-slot-actions'>
            <label htmlFor={inputId} className='pv-slot-btn' title='Trocar'>
              <FontAwesomeIcon icon={faSyncAlt} aria-hidden='true' />
              <span className='pv-sr-only'>Trocar {label}</span>
            </label>
            <button type='button' className='pv-slot-btn' onClick={onRemove} title='Remover'>
              <FontAwesomeIcon icon={faTrashAlt} aria-hidden='true' />
              <span className='pv-sr-only'>Remover {label}</span>
            </button>
          </div>
        </>
      ) : (
        <label htmlFor={inputId} className='pv-slot-empty'>
          <FontAwesomeIcon icon={faCamera} aria-hidden='true' />
          <span>{wide ? 'Adicionar logo' : 'Adicionar foto'}</span>
        </label>
      )}
    </div>
  );
}

// Logo e ate 5 fotos da oficina: o que o cliente ve na pagina da oficina.
export default function PhotosPanel({ company, myData, onSaved }) {
  const [logo, setLogo] = useState(myData.logo ? { url: myData.logo } : null);
  const [photos, setPhotos] = useState(myData.photos);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogo(myData.logo ? { url: myData.logo } : null);
    setPhotos(myData.photos);
  }, [myData]);

  // Libera as previas locais quando a tela sai.
  useEffect(() => () => {
    [logo, ...photos].forEach((slot) => slot && slot.preview && URL.revokeObjectURL(slot.preview));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    const logoChanged = (logo ? logo.url || 'novo' : null) !== (myData.logo || null) || Boolean(logo && logo.file);
    const photosChanged = photos.some((slot, index) => {
      const before = myData.photos[index];
      if (!slot || !before) return slot !== before;
      return Boolean(slot.file) || slot.id !== before.id;
    });
    return logoChanged || photosChanged;
  }, [logo, photos, myData]);

  const setPhoto = (index, value) => setPhotos((list) => list.map((slot, i) => (i === index ? value : slot)));

  const reset = () => {
    setLogo(myData.logo ? { url: myData.logo } : null);
    setPhotos(myData.photos);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveImages(company, myData, { logo, photos });
      toastr.success('Fotos atualizadas.');
      onSaved();
    } catch (err) {
      toastr.error('Não foi possível salvar as fotos.', 'Tente de novo em instantes.');
    } finally {
      setSaving(false);
    }
  };

  const filled = photos.filter(Boolean).length;

  return (
    <section className='pv-panel' aria-labelledby='pv-photos-title'>
      <h2 id='pv-photos-title' className='pv-panel-title'>
        Logo e fotos
        <span className='pv-panel-count'>{filled} de {photos.length} fotos</span>
      </h2>
      <p className='pv-panel-hint'>Logo com ao menos 300×150 px e fotos com 800×600 px, até 5 MB cada.</p>

      <div className='pv-gallery'>
        <Slot slot={logo} label='Logo' min={LOGO_MIN} wide onPick={setLogo} onRemove={() => setLogo(null)} />
        {photos.map((slot, index) => (
          <Slot
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            slot={slot}
            label={`Foto ${index + 1}`}
            min={PHOTO_MIN}
            onPick={(value) => setPhoto(index, value)}
            onRemove={() => setPhoto(index, null)}
          />
        ))}
      </div>

      {dirty && (
        <div className='pv-panel-actions'>
          <button type='button' className='gs-btn gs-btn-secondary' onClick={reset} disabled={saving}>Desfazer</button>
          <button type='button' className='gs-btn gs-btn-primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar fotos'}
          </button>
        </div>
      )}
    </section>
  );
}
